import { isApplianceReady } from '../../../lib/helpers';

/**
 * Check if cloud type is SDM enabled (from RM.isSDMEnabled logic)
 */
export const isSDMEnabled = (cloudType) => {
    // Based on the Backbone logic, most cloud types are SDM enabled except specific ones
    const nonSDMTypes = ['rivermeadow_standalone'];
    return !nonSDMTypes.includes(cloudType);
};

/**
 * Process cloud accounts for dropdown with appliance readiness checks
 */
export const processCloudAccounts = (cloudAccountsData, isMigrateByVM) => {
    if (!cloudAccountsData?.content) return [];

    return cloudAccountsData.content
        .map((account) => ({
            id: account.id,
            appliance_id: account.appliance?.id,
            name: account.name,
            cloud_type: account.type,
            appliance: account.appliance,
            disabled: !isApplianceReady(account.appliance, isMigrateByVM),
        }))
        .sort((a, b) => a.disabled - b.disabled);
};

/**
 * Filter sources that require CA credentials
 */
export const getSourcesWithCACreds = (selectedSources) => {
    return selectedSources.filter((source) => source.credentials?.storage === 'ca');
};

/**
 * Check migration type flags based on selected sources
 */
export const getMigrationTypeFlags = (selectedSources) => {
    return {
        isMigrateByVM: selectedSources.some((source) => source.collection_type === 'vm'),
        isMigrateNetAppFiles: selectedSources.some((source) => source.migrate_netapp_files),
    };
};

/**
 * Validate if selected sources have mixed migration methods
 * Returns true if sources are mixed (both VM-based and OS-based)
 */
export const hasMixedMigrationMethods = (selectedSources) => {
    if (!selectedSources || selectedSources.length === 0) {
        return false;
    }

    const hasVMSources = selectedSources.some((source) => source.collection_type === 'vm');
    const hasOSSources = selectedSources.some(
        (source) => source.collection_type !== 'vm' && source.collection_type !== 'data_store',
    );

    // Mixed if we have both VM and OS-based sources
    return hasVMSources && hasOSSources;
};
/**
 * Validate if Cloud Files (NetApp/data_store) sources are mixed with VM or OS sources
 * Returns true if data_store sources are mixed with VM or OS sources
 */
export const hasCloudFilesWithOtherSources = (selectedSources) => {
    if (!selectedSources || selectedSources.length === 0) {
        return false;
    }

    const hasDataStoreSources = selectedSources.some((source) => source.collection_type === 'data_store');
    const hasVMSources = selectedSources.some((source) => source.collection_type === 'vm');
    const hasOSSources = selectedSources.some(
        (source) => source.collection_type !== 'vm' && source.collection_type !== 'data_store',
    );

    // Can't mix data_store sources with VM or OS sources
    return hasDataStoreSources && (hasVMSources || hasOSSources);
};

/**
 * Determine whether to show "Use stored source information" checkbox
 * Based on asset_list_module.js logic:
 * - showUseStoredInfoCheckbox starts as false
 * - Set to true for data_store sources
 * - Set to true for non-VM, non-data_store sources (OS-based)
 * - Remains false for VM-only sources
 */
export const getShowUseStoredInfoCheckbox = (selectedSources) => {
    return selectedSources.some(
        (source) =>
            source.collection_type === 'data_store' ||
            (source.collection_type !== 'vm' && source.collection_type !== 'data_store'),
    );
};

/*

/**
 * Validate credential sets selection
 */
export const validateCredentialSets = (selectedSourcesWithCACreds, credentialSets) => {
    return selectedSourcesWithCACreds.some((source) => !credentialSets[source.id]);
};

/**
 * Prepare migration readiness data for submission
 */
export const prepareMigrationData = (formValues, selectedAccount, credentialSets, selectedSources) => {
    return {
        cloudAccount: selectedAccount,
        migrationType: formValues.migrationType,
        blockMobilityType: formValues.blockMobilityType,
        fileMobilityType: formValues.fileMobilityType,
        useStoredData: formValues.useStoredInfo || false,
        ignoreValidationErrors: formValues.ignoreValidationErrors || false,
        credentialSets,
        selectedSources,
    };
};
